/**
 * Shared layout services hook.
 * Handles WebSocket initialization and timezone setup used by both layouts.
 */

import { useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from '@/hooks/redux';
import { webSocketSystem, initializeWebSocketSystem } from '@/services/websocket/index';
import { authService } from '@/services/authService';
import timezoneService from '@/services/timezoneService';
import { logger } from '@/utils/logger';

export function useLayoutServices() {
  const dispatch = useAppDispatch();
  const { user } = useAppSelector(state => state.auth);
  const [wsStatus, setWsStatus] = useState<'connecting' | 'connected' | 'disconnected' | 'error'>('disconnected');

  // WebSocket connection management
  useEffect(() => {
    const statusHandler = (status: 'connecting' | 'connected' | 'disconnected' | 'error') => {
      setWsStatus(status);
    };

    statusHandler(webSocketSystem.getConnectionState() as any);

    const statusInterval = setInterval(() => {
      statusHandler(webSocketSystem.getConnectionState() as any);
    }, 10000);

    const token = authService.getAccessToken();
    // Connect when authenticated: a JS token (dual mode) OR an authenticated
    // cookie session (cookie mode has NO JS token — the httpOnly cookie carries
    // auth). Gating only on `token` meant the WS never connected under
    // VITE_COOKIE_AUTH=true.
    const shouldConnect = !!token || (authService.isCookieAuth() && authService.isAuthenticated());

    if (shouldConnect) {
      const connectTimer = setTimeout(async () => {
        try {
          await initializeWebSocketSystem(dispatch, {
            enableMessageDeduplication: true,
            messageTimeout: 5000,
          });
          await webSocketSystem.connect();
        } catch (error) {
          logger.error('Failed to connect WebSocket system', error);
        }
      }, 100);

      return () => {
        clearTimeout(connectTimer);
        clearInterval(statusInterval);
      };
    }

    // Not authenticated (no token, no cookie session) → ensure it's down.
    if (!user && !token) {
      webSocketSystem.disconnect();
    }

    return () => {
      clearInterval(statusInterval);
    };
  }, [user, dispatch]);

  // Timezone setup
  useEffect(() => {
    if (user?.timezone) {
      timezoneService.setUserTimezone(user.timezone);
    }
  }, [user?.timezone]);

  return { wsStatus, user };
}
